import { infiniteQueryOptions, keepPreviousData, mutationOptions, queryOptions } from '@tanstack/react-query';
import { when as rWhen } from 'remeda';

import { client, HttpException, type components, type operations } from '@/libs/openapi';
import { publicationQueries } from '@/resources/publication/publication.api';
import type { PartialExcept } from '@/types/partial-except.type';
import { mutationNamespace, queryNamespace } from '@/utils/query-namespace.util';

const updateArticlesInPage = <T extends { articles: { id: string; excluded: boolean; saved: boolean; }[] }>(
  articleId: string,
  patch: Partial<T['articles'][number]>
) => (page: T) => ({
  ...page,
  articles: page.articles.map(rWhen(({ id }) => id === articleId, (article) => ({ ...article, ...patch }))),
});

const addArticleToList = <T extends { articles: { id: string; saved: boolean; seen?: boolean; }[]; total: number }>(
  pages: T[],
  article: { id: string; saved: boolean; seen?: boolean; [key: string]: unknown }
): T[] => {
  if (pages.length === 0) {
    return [{
      data: {
        articles: [article],
        total: 1,
        page: 1,
        page_size: 1,
        total_pages: 1
      }
    } as unknown as T];
  }

  const newPages = [...pages];

  newPages.unshift({
    ...pages[0],
    articles: [article, ...pages[0].articles],
    total: pages[0].total + 1,
  });

  return newPages;
};

const removeArticleFromSaved = <T extends { articles: { id: string; saved: boolean }[]; total: number }>(
  pages: T[],
  articleId: string
): T[] => pages.map((page) => ({
  ...page,
  articles: page.articles.filter((article) => article.id !== articleId),
  total: Math.max(0, page.total - 1),
}));

export const articleQueries = queryNamespace('article', {
  feed: (data: operations['articles-feed_articles']['parameters']['query']) => infiniteQueryOptions({
    queryKey: [data],
    queryFn: (options) => client
      .GET('/api/v1/articles/feed', {
        params: {
          query: {
            ...data,
            page: options.pageParam,
            page_size: 100,
          }
        },
        signal: options.signal,
      })
      .then(({ data, error, response }) => {
        if (error) {
          throw new HttpException({ message: error.detail?.map(item => item.msg).join(', '), response });
        }

        return {
          ...data,
          articles: data.articles.map((article) => ({
            ...article,
            excluded: false,
          })),
        };
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (!lastPage || lastPage.page === 100 || lastPage.total < lastPage.page_size) {
        return undefined;
      }
      return lastPageParam + 1;
    },
    getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
      if (firstPageParam <= 1) {
        return undefined;
      }
      return firstPageParam - 1;
    },
    placeholderData: keepPreviousData,
  }),
  saved: infiniteQueryOptions({
    queryKey: [],
    queryFn: (options) => client.GET('/api/v1/articles/saved', {
      params: {
        query: {
          page: options.pageParam,
          page_size: 100,
        }
      },
      signal: options.signal,
    }).then(({ data, error, response }) => {
      if (error) {
        throw new HttpException({ message: error.detail?.map(item => item.msg).join(', '), response });
      }

      return {
        ...data,
        articles: data.articles.map((article) => ({
          ...article,
          excluded: false,
        })),
      };
    }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (!lastPage || lastPage.page === 100 || lastPage.total < lastPage.page_size) {
        return undefined;
      }
      return lastPageParam + 1;
    },
    getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
      if (firstPageParam <= 1) {
        return undefined;
      }
      return firstPageParam - 1;
    },
    placeholderData: keepPreviousData,
  }),
  seen: infiniteQueryOptions({
    queryKey: [],
    queryFn: (options) => client.GET('/api/v1/articles/seen', {
      params: {
        query: {
          page: options.pageParam,
          page_size: 100,
        }
      },
      signal: options.signal,
    }).then(({ data, error, response }) => {
      if (error) {
        throw new HttpException({ message: error.detail?.map(item => item.msg).join(', '), response });
      }

      return {
        ...data,
        articles: data.articles.map((article) => ({
          ...article,
          excluded: false,
        })),
      };
    }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (!lastPage || lastPage.page === 100 || lastPage.total < lastPage.page_size) {
        return undefined;
      }
      return lastPageParam + 1;
    },
    getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
      if (firstPageParam <= 1) {
        return undefined;
      }
      return firstPageParam - 1;
    },
    placeholderData: keepPreviousData,
  }),
  view: (data: operations['articles-view_article']['parameters']['path']) => queryOptions({
    queryKey: [data],
    queryFn: (options) => client
      .GET('/api/v1/articles/view/{article_id}', {
        params: {
          path: { article_id: data.article_id },
        },
        signal: options.signal,
      })
      .then(({ data, error, response }) => {
        if (error) {
          throw new HttpException({ message: error.detail?.map(item => item.msg).join(', '), response });
        }

        return data;
      }),
  }),
  similar: (data: operations['articles-similar_articles']['parameters']['path']) => infiniteQueryOptions({
    queryKey: [data],
    queryFn: (options) => client
      .GET('/api/v1/articles/similar/{article_id}', {
        params: {
          path: { article_id: data.article_id },
        },
        signal: options.signal,
      })
      .then(({ data, error, response }) => {
        if (error) {
          throw new HttpException({ message: error.detail?.map(item => item.msg).join(', '), response });
        }

        return {
          ...data,
          articles: data.articles.map((article) => ({ ...article, excluded: false })),
        };
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (!lastPage || lastPage.page === 100 || lastPage.total < lastPage.page_size) {
        return undefined;
      }
      return lastPageParam + 1;
    },
    getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
      if (firstPageParam <= 1) {
        return undefined;
      }
      return firstPageParam - 1;
    },
    placeholderData: keepPreviousData,
  }),
  search: (data: operations['articles-search_articles']['parameters']['query']) => infiniteQueryOptions({
    queryKey: [data],
    queryFn: (options) => client
      .GET('/api/v1/articles/search', {
        params: {
          query: {
            ...data,
            page: options.pageParam,
            page_size: 100,
          }
        },
        signal: options.signal,
      })
      .then(({ data, error, response }) => {
        if (error) {
          throw new HttpException({ message: error.detail?.map(item => item.msg).join(', '), response });
        }

        return {
          ...data,
          articles: data.articles.map((article) => ({
            ...article,
            excluded: false,
          })),
        };
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (!lastPage || lastPage.page === 100 || lastPage.total < lastPage.page_size) {
        return undefined;
      }
      return lastPageParam + 1;
    },
    getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
      if (firstPageParam <= 1) {
        return undefined;
      }
      return firstPageParam - 1;
    },
    placeholderData: keepPreviousData,
  }),
});

export const articleMutations = mutationNamespace('article', {
  save: (
    queryContext?: {
      feed?: Parameters<typeof articleQueries.feed>[0],
      publicationArticles?: Parameters<typeof publicationQueries.articles>[0],
      search?: Parameters<typeof articleQueries.search>[0],
      similar?: Parameters<typeof articleQueries.similar>[0]
    }
  ) => mutationOptions({
    mutationFn: (data: operations['articles-save_article']['parameters']['path']) => client
      .POST('/api/v1/articles/saved/{article_id}', {
        params: {
          path: { article_id: data.article_id },
        },
      })
      .then(({ data, error, response }) => {
        if (error) {
          throw new HttpException({ message: error.detail?.map(item => item.msg).join(', '), response });
        }

        return data;
      }),
    onMutate: async (data, context) => {
      await context.client.cancelQueries({ queryKey: articleQueries.feed.queryKey });

      const previousFeedData = queryContext?.feed && context.client.getQueryData(articleQueries.feed(queryContext.feed).queryKey);
      const previousSavedData = context.client.getQueryData(articleQueries.saved.queryKey);
      const previousSeenData = context.client.getQueryData(articleQueries.seen.queryKey);
      const previousViewData = context.client.getQueryData(articleQueries.view(data).queryKey);
      const previousSimilarData = context.client.getQueryData(articleQueries.similar(data).queryKey);
      const previousPublicationArticlesData = queryContext?.publicationArticles &&
        context.client.getQueryData(publicationQueries.articles(queryContext.publicationArticles).queryKey);
      const previousSearchData = queryContext?.search && context.client.getQueryData(articleQueries.search(queryContext.search).queryKey);

      if (queryContext?.feed) {
        context.client.setQueryData(articleQueries.feed(queryContext.feed).queryKey, (old) => old && {
          ...old,
          pages: old.pages.map(updateArticlesInPage(data.article_id, { saved: true })),
        });
      }

      if (queryContext?.search) {
        context.client.setQueryData(articleQueries.search(queryContext.search).queryKey, (old) => old && {
          ...old,
          pages: old.pages.map(updateArticlesInPage(data.article_id, { saved: true })),
        });
      }

      const articleData = context.client.getQueryData(articleQueries.view(data).queryKey);
      let article: PartialExcept<components['schemas']['ArticleDetail'], 'id'> | undefined = articleData;

      if (!article && queryContext?.feed) {
        const feedData = context.client.getQueryData(articleQueries.feed(queryContext.feed).queryKey);
        if (feedData) {
          article = feedData.pages
            .flatMap(page => page.articles)
            .find(article => article.id === data.article_id);
        }
      }

      if (!article && queryContext?.search) {
        const searchData = context.client.getQueryData(articleQueries.search(queryContext.search).queryKey);
        if (searchData) {
          article = searchData.pages
            .flatMap(page => page.articles)
            .find(article => article.id === data.article_id);
        }
      }

      if (!article && queryContext?.publicationArticles) {
        const publicationData = context.client.getQueryData(publicationQueries.articles(queryContext.publicationArticles).queryKey);
        if (publicationData) {
          article = publicationData.pages
            .flatMap(page => page.articles)
            .find(article => article.id === data.article_id);
        }
      }

      if (article) {
        context.client.setQueryData(articleQueries.saved.queryKey, (old) => old && {
          ...old,
          pages: addArticleToList(old.pages, { ...article, saved: true }),
        });
      }

      if (queryContext?.similar) {
        context.client.setQueryData(articleQueries.similar(queryContext.similar).queryKey, (old) => old && {
          ...old,
          pages: old.pages.map(updateArticlesInPage(data.article_id, { saved: true })),
        });
      }

      context.client.setQueryData(articleQueries.view(data).queryKey, (old) => old && {
        ...old,
        saved: true,
      });

      if (queryContext?.publicationArticles) {
        context.client.setQueryData(publicationQueries.articles(queryContext.publicationArticles).queryKey, (old) => old && {
          ...old,
          pages: old.pages.map(updateArticlesInPage(data.article_id, { saved: true })),
        });
      }

      context.client.setQueryData(articleQueries.seen.queryKey, (old) => old && {
        ...old,
        pages: old.pages.map(updateArticlesInPage(data.article_id, { saved: true })),
      });

      return {
        previousFeedData,
        previousSavedData,
        previousSimilarData,
        previousViewData,
        previousPublicationArticlesData,
        previousSearchData,
        previousSeenData,
      };
    },
    onError: (_error, variables, onMutateResult, context) => {
      if (queryContext?.feed) {
        context.client.setQueryData(articleQueries.feed(queryContext.feed).queryKey, onMutateResult?.previousFeedData);
      }

      context.client.setQueryData(articleQueries.saved.queryKey, onMutateResult?.previousSavedData);
      context.client.setQueryData(articleQueries.view(variables).queryKey, onMutateResult?.previousViewData);
      context.client.setQueryData(articleQueries.seen.queryKey, onMutateResult?.previousSeenData);

      if (queryContext?.similar) {
        context.client.setQueryData(articleQueries.similar(queryContext.similar).queryKey, onMutateResult?.previousSimilarData);
      }

      if (queryContext?.search) {
        context.client.setQueryData(articleQueries.search(queryContext.search).queryKey, onMutateResult?.previousSearchData);
      }

      if (queryContext?.publicationArticles) {
        context.client.setQueryData(publicationQueries.articles(queryContext.publicationArticles).queryKey, onMutateResult?.previousPublicationArticlesData);
      }
    },
    onSuccess: (_data, _variables, _onMutateResult, context) => {
      // invalidate the feed only if the user is not on the feed page, so that saved articles are still displayed
      if (!queryContext?.feed) {
        context.client.invalidateQueries({ queryKey: articleQueries.feed.queryKey });
      }

      // invalidate the publication articles only if the user is not on the publication articles page, so that saved articles are still displayed
      if (!queryContext?.publicationArticles) {
        context.client.invalidateQueries({ queryKey: publicationQueries.articles.queryKey });
      }

      // invalidate the search only if the user is not on the search page, so that saved articles are still displayed
      if (!queryContext?.search) {
        context.client.invalidateQueries({ queryKey: articleQueries.search.queryKey });
      }

      // invalidate the similar articles only if the user is not on the similar articles page, so that saved articles are still displayed
      if (!queryContext?.similar) {
        context.client.invalidateQueries({ queryKey: articleQueries.similar.queryKey });
      }

      context.client.invalidateQueries({ queryKey: articleQueries.seen.queryKey });
    },
  }),
  unsave: (
    queryContext?: {
      feed?: Parameters<typeof articleQueries.feed>[0],
      publicationArticles?: Parameters<typeof publicationQueries.articles>[0],
      search?: Parameters<typeof articleQueries.search>[0],
      similar?: Parameters<typeof articleQueries.similar>[0]
    }
  ) => mutationOptions({
    mutationFn: (data: operations['articles-remove_excluded_article']['parameters']['path']) => client
      .DELETE('/api/v1/articles/saved/{article_id}', {
        params: {
          path: { article_id: data.article_id },
        },
      })
      .then(({ data, error, response }) => {
        if (error) {
          throw new HttpException({ message: error.detail?.map(item => item.msg).join(', '), response });
        }

        return data;
      }),
    onMutate: async (data, context) => {
      await context.client.cancelQueries({ queryKey: articleQueries.feed.queryKey });

      const previousFeedData = queryContext?.feed && context.client.getQueryData(articleQueries.feed(queryContext.feed).queryKey);
      const previousSavedData = context.client.getQueryData(articleQueries.saved.queryKey);
      const previousViewData = context.client.getQueryData(articleQueries.view(data).queryKey);
      const previousSimilarData = context.client.getQueryData(articleQueries.similar(data).queryKey);
      const previousPublicationArticlesData = queryContext?.publicationArticles &&
        context.client.getQueryData(publicationQueries.articles(queryContext.publicationArticles).queryKey);
      const previousSearchData = queryContext?.search && context.client.getQueryData(articleQueries.search(queryContext.search).queryKey);
      const previousSeenData = context.client.getQueryData(articleQueries.seen.queryKey);

      if (queryContext?.feed) {
        context.client.setQueryData(articleQueries.feed(queryContext.feed).queryKey, (old) => old && {
          ...old,
          pages: old.pages.map(updateArticlesInPage(data.article_id, { saved: false })),
        });
      }

      if (queryContext?.search) {
        context.client.setQueryData(articleQueries.search(queryContext.search).queryKey, (old) => old && {
          ...old,
          pages: old.pages.map(updateArticlesInPage(data.article_id, { saved: false })),
        });
      }

      context.client.setQueryData(articleQueries.saved.queryKey, (old) => old && {
        ...old,
        pages: removeArticleFromSaved(old.pages, data.article_id),
      });

      if (queryContext?.similar) {
        context.client.setQueryData(articleQueries.similar(queryContext.similar).queryKey, (old) => old && {
          ...old,
          pages: old.pages.map(updateArticlesInPage(data.article_id, { saved: false })),
        });
      }

      context.client.setQueryData(articleQueries.view(data).queryKey, (old) => old && {
        ...old,
        saved: false,
      });

      if (queryContext?.publicationArticles) {
        context.client.setQueryData(publicationQueries.articles(queryContext.publicationArticles).queryKey, (old) => old && {
          ...old,
          pages: old.pages.map(updateArticlesInPage(data.article_id, { saved: false })),
        });
      }

      context.client.setQueryData(articleQueries.seen.queryKey, (old) => old && {
        ...old,
        pages: old.pages.map(updateArticlesInPage(data.article_id, { saved: false })),
      });

      return {
        previousFeedData,
        previousSavedData,
        previousSimilarData,
        previousViewData,
        previousPublicationArticlesData,
        previousSearchData,
        previousSeenData,
      };
    },
    onError: (_error, variables, onMutateResult, context) => {
      if (queryContext?.feed) {
        context.client.setQueryData(articleQueries.feed(queryContext.feed).queryKey, onMutateResult?.previousFeedData);
      }

      context.client.setQueryData(articleQueries.saved.queryKey, onMutateResult?.previousSavedData);
      context.client.setQueryData(articleQueries.view(variables).queryKey, onMutateResult?.previousViewData);
      context.client.setQueryData(articleQueries.seen.queryKey, onMutateResult?.previousSeenData);

      if (queryContext?.similar) {
        context.client.setQueryData(articleQueries.similar(queryContext.similar).queryKey, onMutateResult?.previousSimilarData);
      }

      if (queryContext?.search) {
        context.client.setQueryData(articleQueries.search(queryContext.search).queryKey, onMutateResult?.previousSearchData);
      }

      if (queryContext?.publicationArticles) {
        context.client.setQueryData(publicationQueries.articles(queryContext.publicationArticles).queryKey, onMutateResult?.previousPublicationArticlesData);
      }
    },
    onSuccess: (_data, _variables, _onMutateResult, context) => {
      // invalidate the feed only if the user is not on the feed page, so that unsaved articles are still displayed
      if (!queryContext?.feed) {
        context.client.invalidateQueries({ queryKey: articleQueries.feed.queryKey });
      }

      // invalidate the publication articles only if the user is not on the publication articles page, so that unsaved articles are still displayed
      if (!queryContext?.publicationArticles) {
        context.client.invalidateQueries({ queryKey: publicationQueries.articles.queryKey });
      }

      // invalidate the search only if the user is not on the search page, so that unsaved articles are still displayed
      if (!queryContext?.search) {
        context.client.invalidateQueries({ queryKey: articleQueries.search.queryKey });
      }

      // invalidate the similar articles only if the user is not on the similar articles page, so that unsaved articles are still displayed
      if (!queryContext?.similar) {
        context.client.invalidateQueries({ queryKey: articleQueries.similar.queryKey });
      }

      context.client.invalidateQueries({ queryKey: articleQueries.seen.queryKey });
    },
  }),
  exclude: (
    queryContext?: {
      feed?: Parameters<typeof articleQueries.feed>[0],
      publicationArticles?: Parameters<typeof publicationQueries.articles>[0],
      search?: Parameters<typeof articleQueries.search>[0],
      similar?: Parameters<typeof articleQueries.similar>[0]
    }
  ) => mutationOptions({
    mutationFn: (data: operations['articles-exclude_article']['parameters']['path']) => client
      .POST('/api/v1/articles/excluded/{article_id}', {
        params: {
          path: { article_id: data.article_id },
        },
      })
      .then(({ data, error, response }) => {
        if (error) {
          throw new HttpException({ message: error.detail?.map(item => item.msg).join(', '), response });
        }

        return data;
      }),
    onMutate: async (data, context) => {
      await context.client.cancelQueries({ queryKey: articleQueries.feed.queryKey });

      const previousFeedData = queryContext?.feed && context.client.getQueryData(articleQueries.feed(queryContext.feed).queryKey);
      const previousSavedData = context.client.getQueryData(articleQueries.saved.queryKey);
      const previousViewData = context.client.getQueryData(articleQueries.view(data).queryKey);
      const previousSimilarData = context.client.getQueryData(articleQueries.similar(data).queryKey);
      const previousPublicationArticlesData = queryContext?.publicationArticles && context.client.getQueryData(
        publicationQueries.articles(queryContext.publicationArticles).queryKey);
      const previousSearchData = queryContext?.search && context.client.getQueryData(articleQueries.search(queryContext.search).queryKey);
      const previousSeenData = context.client.getQueryData(articleQueries.seen.queryKey);

      if (queryContext?.feed) {
        context.client.setQueryData(articleQueries.feed(queryContext.feed).queryKey, (old) => old && {
          ...old,
          pages: old.pages.map(updateArticlesInPage(data.article_id, { excluded: true })),
        });
      }

      if (queryContext?.search) {
        context.client.setQueryData(articleQueries.search(queryContext.search).queryKey, (old) => old && {
          ...old,
          pages: old.pages.map(updateArticlesInPage(data.article_id, { excluded: true })),
        });
      }

      context.client.setQueryData(articleQueries.saved.queryKey, (old) => old && {
        ...old,
        pages: old.pages.map(updateArticlesInPage(data.article_id, { excluded: true })),
      });

      if (queryContext?.similar) {
        context.client.setQueryData(articleQueries.similar(queryContext.similar).queryKey, (old) => old && {
          ...old,
          pages: old.pages.map(updateArticlesInPage(data.article_id, { excluded: true })),
        });
      }

      context.client.setQueryData(articleQueries.view(data).queryKey, (old) => old && {
        ...old,
        excluded: true,
      });

      if (queryContext?.publicationArticles) {
        context.client.setQueryData(publicationQueries.articles(queryContext.publicationArticles).queryKey, (old) => old && {
          ...old,
          pages: old.pages.map(updateArticlesInPage(data.article_id, { excluded: true })),
        });
      }

      context.client.setQueryData(articleQueries.seen.queryKey, (old) => old && {
        ...old,
        pages: old.pages.map(updateArticlesInPage(data.article_id, { excluded: true })),
      });

      return {
        previousFeedData,
        previousSavedData,
        previousSimilarData,
        previousViewData,
        previousPublicationArticlesData,
        previousSearchData,
        previousSeenData,
      };
    },
    onError: (_error, variables, onMutateResult, context) => {
      if (queryContext?.feed) {
        context.client.setQueryData(articleQueries.feed(queryContext.feed).queryKey, onMutateResult?.previousFeedData);
      }

      context.client.setQueryData(articleQueries.saved.queryKey, onMutateResult?.previousSavedData);
      context.client.setQueryData(articleQueries.view(variables).queryKey, onMutateResult?.previousViewData);
      context.client.setQueryData(articleQueries.seen.queryKey, onMutateResult?.previousSeenData);

      if (queryContext?.similar) {
        context.client.setQueryData(articleQueries.similar(queryContext.similar).queryKey, onMutateResult?.previousSimilarData);
      }

      if (queryContext?.search) {
        context.client.setQueryData(articleQueries.search(queryContext.search).queryKey, onMutateResult?.previousSearchData);
      }

      if (queryContext?.publicationArticles) {
        context.client.setQueryData(publicationQueries.articles(queryContext.publicationArticles).queryKey, onMutateResult?.previousPublicationArticlesData);
      }
    },
    onSuccess: (_data, _variables, _onMutateResult, context) => {
      // invalidate the feed only if the user is not on the feed page, so that excluded articles are still displayed
      if (!queryContext?.feed) {
        context.client.invalidateQueries({ queryKey: articleQueries.feed.queryKey });
      }

      // invalidate the publication articles only if the user is not on the publication articles page, so that excluded articles are still displayed
      if (!queryContext?.publicationArticles) {
        context.client.invalidateQueries({ queryKey: publicationQueries.articles.queryKey });
      }

      // invalidate the search only if the user is not on the search page, so that excluded articles are still displayed
      if (!queryContext?.search) {
        context.client.invalidateQueries({ queryKey: articleQueries.search.queryKey });
      }

      // invalidate the similar articles only if the user is not on the similar articles page, so that excluded articles are still displayed
      if (!queryContext?.similar) {
        context.client.invalidateQueries({ queryKey: articleQueries.similar.queryKey });
      }

      context.client.invalidateQueries({ queryKey: articleQueries.seen.queryKey });
    }
  }),
  removeExclude: (
    queryContext?: {
      feed?: Parameters<typeof articleQueries.feed>[0],
      publicationArticles?: Parameters<typeof publicationQueries.articles>[0],
      search?: Parameters<typeof articleQueries.search>[0],
      similar?: Parameters<typeof articleQueries.similar>[0]
    }
  ) => mutationOptions({
    mutationFn: (data: operations['articles-remove_excluded_article']['parameters']['path']) => client
      .DELETE('/api/v1/articles/excluded/{article_id}', {
        params: {
          path: { article_id: data.article_id },
        },
      })
      .then(({ data, error, response }) => {
        if (error) {
          throw new HttpException({ message: error.detail?.map(item => item.msg).join(', '), response });
        }

        return data;
      }),
    onMutate: async (data, context) => {
      await context.client.cancelQueries({ queryKey: articleQueries.feed.queryKey });

      const previousFeedData = queryContext?.feed && context.client.getQueryData(articleQueries.feed(queryContext.feed).queryKey);
      const previousSavedData = context.client.getQueryData(articleQueries.saved.queryKey);
      const previousViewData = context.client.getQueryData(articleQueries.view(data).queryKey);
      const previousSimilarData = context.client.getQueryData(articleQueries.similar(data).queryKey);
      const previousPublicationArticlesData = queryContext?.publicationArticles &&
        context.client.getQueryData(publicationQueries.articles(queryContext.publicationArticles).queryKey);
      const previousSearchData = queryContext?.search && context.client.getQueryData(articleQueries.search(queryContext.search).queryKey);
      const previousSeenData = context.client.getQueryData(articleQueries.seen.queryKey);

      if (queryContext?.feed) {
        context.client.setQueryData(articleQueries.feed(queryContext.feed).queryKey, (old) => old && {
          ...old,
          pages: old.pages.map(updateArticlesInPage(data.article_id, { excluded: false })),
        });
      }

      if (queryContext?.search) {
        context.client.setQueryData(articleQueries.search(queryContext.search).queryKey, (old) => old && {
          ...old,
          pages: old.pages.map(updateArticlesInPage(data.article_id, { excluded: false })),
        });
      }

      context.client.setQueryData(articleQueries.saved.queryKey, (old) => old && {
        ...old,
        pages: old.pages.map(updateArticlesInPage(data.article_id, { excluded: false })),
      });

      context.client.setQueryData(articleQueries.view(data).queryKey, (old) => old && {
        ...old,
        excluded: false,
      });

      if (queryContext?.similar) {
        context.client.setQueryData(articleQueries.similar(queryContext.similar).queryKey, (old) => old && {
          ...old,
          pages: old.pages.map(updateArticlesInPage(data.article_id, { excluded: false })),
        });
      }

      if (queryContext?.publicationArticles) {
        context.client.setQueryData(publicationQueries.articles(queryContext.publicationArticles).queryKey, (old) => old && {
          ...old,
          pages: old.pages.map(updateArticlesInPage(data.article_id, { excluded: false })),
        });
      }

      context.client.setQueryData(articleQueries.seen.queryKey, (old) => old && {
        ...old,
        pages: old.pages.map(updateArticlesInPage(data.article_id, { excluded: false })),
      });

      return {
        previousFeedData,
        previousSavedData,
        previousViewData,
        previousSimilarData,
        previousPublicationArticlesData,
        previousSearchData,
        previousSeenData,
      };
    },
    onError: (_error, variables, onMutateResult, context) => {
      if (queryContext?.feed) {
        context.client.setQueryData(articleQueries.feed(queryContext.feed).queryKey, onMutateResult?.previousFeedData);
      }

      context.client.setQueryData(articleQueries.saved.queryKey, onMutateResult?.previousSavedData);
      context.client.setQueryData(articleQueries.view(variables).queryKey, onMutateResult?.previousViewData);
      context.client.setQueryData(articleQueries.seen.queryKey, onMutateResult?.previousSeenData);

      if (queryContext?.similar) {
        context.client.setQueryData(articleQueries.similar(queryContext.similar).queryKey, onMutateResult?.previousSimilarData);
      }

      if (queryContext?.search) {
        context.client.setQueryData(articleQueries.search(queryContext.search).queryKey, onMutateResult?.previousSearchData);
      }

      if (queryContext?.publicationArticles) {
        context.client.setQueryData(publicationQueries.articles(queryContext.publicationArticles).queryKey, onMutateResult?.previousPublicationArticlesData);
      }
    },
    onSuccess: (_data, _variables, _onMutateResult, context) => {
      // invalidate the feed only if the user is not on the feed page, so that excluded articles are still displayed
      if (!queryContext?.feed) {
        context.client.invalidateQueries({ queryKey: articleQueries.feed.queryKey });
      }

      // invalidate the publication articles only if the user is not on the publication articles page, so that excluded articles are still displayed
      if (!queryContext?.publicationArticles) {
        context.client.invalidateQueries({ queryKey: publicationQueries.articles.queryKey });
      }

      // invalidate the search only if the user is not on the search page, so that excluded articles are still displayed
      if (!queryContext?.search) {
        context.client.invalidateQueries({ queryKey: articleQueries.search.queryKey });
      }

      // invalidate the similar articles only if the user is not on the similar articles page, so that excluded articles are still displayed
      if (!queryContext?.similar) {
        context.client.invalidateQueries({ queryKey: articleQueries.similar.queryKey });
      }

      context.client.invalidateQueries({ queryKey: articleQueries.seen.queryKey });
    }
  }),
  markAsSeen: (
    queryContext?: {
      publicationArticles?: Parameters<typeof publicationQueries.articles>[0],
      similar?: Parameters<typeof articleQueries.similar>[0],
    }
  ) => mutationOptions({
    mutationFn: (data: operations['articles-view_article']['parameters']['path']) => client
      .POST('/api/v1/articles/seen/{article_id}', {
        params: {
          path: { article_id: data.article_id },
        },
      })
      .then(({ data, error, response }) => {
        if (error) {
          throw new HttpException({ message: error.detail?.map(item => item.msg).join(', '), response });
        }

        return data;
      }),
    onMutate: async (data, context) => {
      await context.client.cancelQueries({ queryKey: articleQueries.feed.queryKey });

      const previousSavedData = context.client.getQueryData(articleQueries.saved.queryKey);
      const previousPublicationArticlesData = queryContext?.publicationArticles &&
        context.client.getQueryData(publicationQueries.articles(queryContext.publicationArticles).queryKey);
      const previousSeenData = context.client.getQueryData(articleQueries.seen.queryKey);
      const previousSimilarData = queryContext?.similar && context.client.getQueryData(articleQueries.similar(queryContext.similar).queryKey);
      const article = context.client.getQueryData(articleQueries.view(data).queryKey)!;

      context.client.setQueryData(articleQueries.view(data).queryKey, (old) => old && {
        ...old,
        seen: true,
      });

      if (queryContext?.publicationArticles) {
        context.client.setQueryData(publicationQueries.articles(queryContext.publicationArticles).queryKey, (old) => old && {
          ...old,
          pages: old.pages.map(updateArticlesInPage(data.article_id, { seen: true })),
        });
      }

      if (queryContext?.similar) {
        context.client.setQueryData(articleQueries.similar(queryContext.similar).queryKey, (old) => old && {
          ...old,
          pages: old.pages.map(updateArticlesInPage(data.article_id, { seen: true })),
        });
      }

      context.client.setQueryData(articleQueries.saved.queryKey, (old) => old && {
        ...old,
        pages: old.pages.map(updateArticlesInPage(data.article_id, { seen: true })),
      });

      context.client.setQueryData(articleQueries.seen.queryKey, (old) => old && {
        ...old,
        pages: addArticleToList(old.pages, { ...article, seen: true }),
      });

      return {
        previousSavedData,
        previousSeenData,
        previousPublicationArticlesData,
        previousSimilarData,
      };
    },
    onError: (_error, _variables, onMutateResult, context) => {
      context.client.setQueryData(articleQueries.saved.queryKey, onMutateResult?.previousSavedData);
      context.client.setQueryData(articleQueries.seen.queryKey, onMutateResult?.previousSeenData);

      if (queryContext?.publicationArticles) {
        context.client.setQueryData(publicationQueries.articles(queryContext.publicationArticles).queryKey, onMutateResult?.previousPublicationArticlesData);
      }

      if (queryContext?.similar) {
        context.client.setQueryData(articleQueries.similar(queryContext.similar).queryKey, onMutateResult?.previousSimilarData);
      }

      context.client.setQueryData(articleQueries.saved.queryKey, onMutateResult?.previousSavedData);
      context.client.setQueryData(articleQueries.seen.queryKey, onMutateResult?.previousSeenData);
    },
    onSuccess: (_data, _variables, _onMutateResult, context) => {
      context.client.invalidateQueries({ queryKey: publicationQueries.articles.queryKey });
      context.client.invalidateQueries({ queryKey: articleQueries.similar.queryKey });
      context.client.invalidateQueries({ queryKey: articleQueries.feed.queryKey });
      context.client.invalidateQueries({ queryKey: articleQueries.saved.queryKey });
      context.client.invalidateQueries({ queryKey: articleQueries.seen.queryKey });
    },
  }),
});
